import { useState, useEffect } from "react";
import { chat } from "../lib/api";

const API_BASE = import.meta.env.VITE_AI_GATEWAY_URL || "";

function headers(): Record<string, string> {
  const h: Record<string, string> = { "content-type": "application/json" };
  const token = localStorage.getItem("infra_token");
  if (token) h["authorization"] = `Bearer ${token}`;
  return h;
}

async function api<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: "include", ...opts, headers: { ...headers(), ...(opts?.headers as Record<string, string> || {}) } });
  if (!res.ok) { const body = await res.json().catch(() => ({ error: res.statusText })); throw new Error(body.error || `API error ${res.status}`); }
  return res.json();
}

interface Task {
  id: string;
  product: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  project_id: string | null;
  due_date: string | null;
  tags: string[];
  created_at: string;
  completed_at: string | null;
}

interface Project {
  id: string;
  product: string;
  name: string;
  description: string | null;
  status: string;
  priority: string;
  color: string;
  active_task_count: number;
  done_task_count: number;
}

const PRIORITY_COLORS: Record<string, string> = {
  critical: "bg-red-500/10 text-red-400 border-red-500/20",
  high: "bg-orange-500/10 text-orange-400 border-orange-500/20",
  medium: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  low: "bg-muted text-muted-foreground border-border",
};

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-muted-foreground/20",
  in_progress: "bg-primary",
  done: "bg-success",
};

export function TasksView() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "pending" | "in_progress" | "done">("all");
  const [projectFilter, setProjectFilter] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [newPriority, setNewPriority] = useState("medium");
  const [newDueDate, setNewDueDate] = useState("");
  const [adding, setAdding] = useState(false);
  const [tab, setTab] = useState<"tasks" | "projects">("tasks");

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Use the gateway tools via chat API — but for direct data, call the gateway's tool endpoints
      // Actually, we need to call the productivity service through the gateway
      // The simplest path: use the /v1/chat endpoint with tool calls, OR proxy through the AI worker
      // For now, let's call the productivity tools via a lightweight chat request
      const [taskRes, projRes] = await Promise.all([
        api<any>("/v1/chat", {
          method: "POST",
          body: JSON.stringify({
            messages: [{ role: "user", content: "List all my tasks" }],
            route_mode: "chat.fast",
            system_prompt: "You are a task manager. Call the task_list tool and return the raw JSON result. Do not add commentary.",
            tools: true,
          }),
        }).catch(() => null),
        api<any>("/v1/chat", {
          method: "POST",
          body: JSON.stringify({
            messages: [{ role: "user", content: "List all my projects" }],
            route_mode: "chat.fast",
            system_prompt: "You are a task manager. Call the project_list tool and return the raw JSON result. Do not add commentary.",
            tools: true,
          }),
        }).catch(() => null),
      ]);

      // Parse tool results from chat responses
      if (taskRes?.tools_used?.length) {
        try {
          const raw = JSON.parse(taskRes.tools_used[0].result_preview || "{}");
          if (raw.tasks) setTasks(raw.tasks);
        } catch {}
      }
      if (projRes?.tools_used?.length) {
        try {
          const raw = JSON.parse(projRes.tools_used[0].result_preview || "{}");
          if (raw.projects) setProjects(raw.projects);
        } catch {}
      }
    } catch {
      // fallback: empty
    } finally {
      setLoading(false);
    }
  };

  const addTask = async () => {
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      await api<any>("/v1/chat", {
        method: "POST",
        body: JSON.stringify({
          messages: [{ role: "user", content: `Create a task: "${newTitle.trim()}"${newPriority !== "medium" ? `, priority: ${newPriority}` : ""}${newDueDate ? `, due: ${newDueDate}` : ""}` }],
          route_mode: "chat.fast",
          system_prompt: "You are a task manager. Call the task_create tool with the given parameters. Confirm briefly.",
          tools: true,
        }),
      });
      setNewTitle("");
      setNewDueDate("");
      await loadData();
    } catch {} finally {
      setAdding(false);
    }
  };

  const completeTask = async (id: string) => {
    setTasks((prev) => prev.map((t) => t.id === id ? { ...t, status: "done" } : t));
    try {
      await api<any>("/v1/chat", {
        method: "POST",
        body: JSON.stringify({
          messages: [{ role: "user", content: `Mark task ${id} as done` }],
          route_mode: "chat.fast",
          system_prompt: "Call the task_complete tool with the given ID. Confirm briefly.",
          tools: true,
        }),
      });
    } catch {}
  };

  const filtered = tasks.filter((t) => {
    if (filter !== "all" && t.status !== filter) return false;
    if (projectFilter && t.project_id !== projectFilter) return false;
    return true;
  });

  const activeCount = tasks.filter((t) => t.status !== "done").length;
  const overdueCount = tasks.filter((t) => t.status !== "done" && t.due_date && t.due_date < new Date().toISOString().slice(0, 10)).length;

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-foreground">Tasks</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {activeCount} active{overdueCount > 0 ? ` · ${overdueCount} overdue` : ""} · {projects.length} projects
            </p>
          </div>
          <div className="flex gap-1 bg-card rounded-lg p-1 border border-border">
            {(["tasks", "projects"] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${tab === t ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"}`}
              >
                {t === "tasks" ? "Tasks" : "Projects"}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-muted-foreground/30 border-t-primary rounded-full animate-spin" />
          </div>
        ) : tab === "tasks" ? (
          <>
            {/* Quick Add */}
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex gap-2">
                <input
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addTask()}
                  placeholder="Add a task..."
                  className="flex-1 bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <select
                  value={newPriority}
                  onChange={(e) => setNewPriority(e.target.value)}
                  className="bg-background border border-border rounded-lg px-2 py-2 text-sm text-foreground"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
                <input
                  type="date"
                  value={newDueDate}
                  onChange={(e) => setNewDueDate(e.target.value)}
                  className="bg-background border border-border rounded-lg px-2 py-2 text-sm text-foreground"
                />
                <button
                  onClick={addTask}
                  disabled={!newTitle.trim() || adding}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  {adding ? "..." : "Add"}
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-2 flex-wrap">
              {(["all", "pending", "in_progress", "done"] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setFilter(s)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filter === s ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground border border-border hover:text-foreground"}`}
                >
                  {s === "all" ? "All" : s === "in_progress" ? "In Progress" : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
              {projects.length > 0 && (
                <select
                  value={projectFilter || ""}
                  onChange={(e) => setProjectFilter(e.target.value || null)}
                  className="bg-card border border-border rounded-full px-3 py-1 text-xs text-foreground"
                >
                  <option value="">All Projects</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              )}
            </div>

            {/* Todo List */}
            <div className="space-y-1">
              {filtered.length === 0 ? (
                <div className="bg-card border border-border rounded-xl p-8 text-center text-muted-foreground">
                  {tasks.length === 0 ? "No tasks yet. Add one above or ask the AI." : "No matching tasks."}
                </div>
              ) : filtered.map((task) => (
                <div key={task.id} className="bg-card border border-border rounded-lg px-4 py-3 flex items-start gap-3">
                  <button
                    onClick={() => task.status !== "done" && completeTask(task.id)}
                    className={`mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${task.status === "done" ? "border-success bg-success" : "border-muted-foreground/30 hover:border-primary"}`}
                  >
                    {task.status === "done" && <span className="text-white text-xs">✓</span>}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${task.status === "done" ? "text-muted-foreground line-through" : "text-foreground"}`}>
                      {task.title}
                    </div>
                    {task.description && <p className="text-xs text-muted-foreground mt-0.5">{task.description}</p>}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className={`px-1.5 py-0 text-[10px] rounded border ${PRIORITY_COLORS[task.priority]}`}>{task.priority}</span>
                      {task.due_date && (
                        <span className={`text-[10px] ${task.due_date < new Date().toISOString().slice(0, 10) && task.status !== "done" ? "text-red-400" : "text-muted-foreground"}`}>
                          {task.due_date < new Date().toISOString().slice(0, 10) && task.status !== "done" ? "⚠ " : ""}{task.due_date}
                        </span>
                      )}
                      {task.product !== "ai" && <span className="text-[10px] text-accent">{task.product}</span>}
                      {task.tags?.map((tag) => <span key={tag} className="text-[10px] text-muted-foreground">#{tag}</span>)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          /* Projects Tab */
          <div className="space-y-3">
            {projects.length === 0 ? (
              <div className="bg-card border border-border rounded-xl p-8 text-center text-muted-foreground">
                No projects yet. Ask the AI to create one.
              </div>
            ) : projects.map((project) => (
              <div key={project.id} className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: project.color }} />
                    <h3 className="font-medium text-foreground">{project.name}</h3>
                    <span className={`px-1.5 py-0 text-[10px] rounded border ${PRIORITY_COLORS[project.priority]}`}>{project.priority}</span>
                  </div>
                  <span className={`text-xs ${project.status === "active" ? "text-success" : "text-muted-foreground"}`}>{project.status}</span>
                </div>
                {project.description && <p className="text-xs text-muted-foreground mb-2">{project.description}</p>}
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>{project.active_task_count} active</span>
                  <span>{project.done_task_count} done</span>
                  {project.product !== "ai" && <span className="text-accent">{project.product}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
